// src/features/patient/care-treatment/components/NoteViewerView.tsx

/**
 * Purpose : One clinical note: the text as written (paragraphs and tables as
 * printed, HTML cleaned, or OCR text labelled as such) with its signature
 * line, the original file beside it or instead of it, previous / next note
 * within the visit day, search matches highlighted, and what we know about the note (About
 * this note and the one Source box).
 *
 * Contents :
 *
 * * function(NoteViewerView) : the note page
 * * function(NoteBody) : the note's sections by format, signature and footer
 * * function(SectionContent) : one section's blocks (paragraphs, tables) or body
 * * function(Highlighted) : text with the search words marked
 * * function(AboutRow) : one "About this note" line
 */

import { Fragment, useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { Icon } from "@iconify/react";
import { Button } from "@/shared/components/button";
import { EmptyState } from "@/shared/components/EmptyState";
import { PageBackButton } from "@/shared/components/PageBackButton";
import { PillTabs } from "@/shared/components/PillTabs";
import { cn } from "@/shared/lib/utils";
import { LG_QUERY } from "@/shared/hooks/useMediaQuery";
import type { Encounter, Note, NoteBodyFormat, NoteSection, Source } from "@/features/patient/care-treatment/api/careTimelineTypes";
import {
  formatDateOnly,
  formatNoteWhen,
  noteTypeLabel,
} from "@/features/patient/care-treatment/utils/careTimeline";
import { recordTypeLabel } from "@/features/patient/care-treatment/utils/careNaming";
import { sanitizeNoteHtml } from "@/features/patient/care-treatment/utils/sanitizeNoteHtml";
import { highlightHtml, splitMatches } from "@/features/patient/care-treatment/utils/highlightText";
import { reflowText } from "@/features/patient/care-treatment/utils/noteText";
import { WhereFrom, markClass } from "./careParts";
import { OriginalViewer } from "./OriginalViewer";
import {
  bodyTextClass,
  cardClass,
  concernLabelClass,
  dividerClass,
  faintTextClass,
  inferredNoticeClass,
  navyChipClass,
  outlineButtonClass,
  pageClass,
  redIconBoxClass,
  sectionHeadingClass,
  subTextClass,
  textLinkClass,
  titleClass,
} from "./careStyles";

type ViewMode = "note" | "original" | "both";

const VIEW_MODES: { id: ViewMode; label: string }[] = [
  { id: "note", label: "Note" },
  { id: "original", label: "Original" },
  { id: "both", label: "Side by side" },
];

// Formatted HTML from a hospital: the platform's text styles applied to the elements it may contain.
const htmlBodyClass = cn(
  bodyTextClass,
  "leading-relaxed [&_a]:text-[#E4002B] [&_a]:underline [&_h1]:mb-1 [&_h1]:mt-3 [&_h1]:font-bold [&_h2]:mb-1 [&_h2]:mt-3 [&_h2]:font-bold [&_h3]:mb-1 [&_h3]:mt-3 [&_h3]:font-bold [&_h1]:text-[#0A2947] [&_h2]:text-[#0A2947] [&_h3]:text-[#0A2947] [&_li]:mb-1 [&_ol]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-2 [&_p:last-child]:mb-0 [&_sup]:text-[10px] [&_sup]:text-[#9CA3AF] [&_ul]:mb-2 [&_ul]:list-disc [&_ul]:pl-5",
  // Tables (social history, vitals): a light grid with a grey header row, scrolling sideways if wide.
  "[&_table]:my-3 [&_table]:block [&_table]:w-full [&_table]:max-w-full [&_table]:overflow-x-auto [&_table]:border-collapse [&_table]:text-[13px] [&_td]:border [&_td]:border-[#E5E7EB] [&_td]:px-3 [&_td]:py-2 [&_td]:align-top [&_th]:border [&_th]:border-[#E5E7EB] [&_th]:bg-[#F3F4F6] [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold [&_th]:text-[#6B7280]",
);

/** Paragraphs at a readable width (about 75 characters). */
const readableClass = "max-w-[75ch]";

const sectionId = (key: string) => `note-section-${key}`;

/** Desktop width (lg) when the page opens; where it can't be told, desktop. */
function isDesktopWidth(): boolean {
  return typeof window === "undefined" || typeof window.matchMedia !== "function" || window.matchMedia(LG_QUERY).matches;
}

// function(NoteViewerView)
/**
 * The note page.
 *
 * @param note - The note.
 * @param encounter - Its visit, if it has one (for the back label and "About this note").
 * @param actingAsProfileId - The dependent being viewed, for the original file.
 * @param onBack - Back to where the note was opened from.
 * @param backLabel - What "back" returns to; defaults to the visit.
 * @param previous / next - The neighbouring notes of the visit day (null at the ends).
 * @param onOpenNote - Open a neighbouring note.
 * @param onOpenVisit - Open the note's visit from "About this note".
 * @param highlight - Search terms to mark; the page scrolls to the first.
 */
export function NoteViewerView({
  note, encounter, actingAsProfileId, onBack, backLabel, previous, next, onOpenNote, onOpenVisit, highlight = [],
}: {
  note: Note;
  encounter: Encounter | null;
  actingAsProfileId?: string;
  onBack: () => void;
  backLabel?: string;
  previous?: Note | null;
  next?: Note | null;
  onOpenNote?: (note: Note) => void;
  onOpenVisit?: () => void;
  highlight?: string[];
}) {
  const hasText = note.sections.length > 0;
  // Text that may not have been read correctly, or read from a scan, opens beside its original (desktop).
  const unsure = note.textQuality === "broken_font";
  const hasOriginal = note.sources.some((s) => s.originalRef);
  const [mode, setMode] = useState<ViewMode>(() => {
    if (!hasText) return "original";
    return (unsure || note.bodyFormat === "ocr") && hasOriginal && isDesktopWidth() ? "both" : "note";
  });
  const [sourceIndex, setSourceIndex] = useState(0);
  const textRef = useRef<HTMLElement>(null);
  const type = noteTypeLabel(note);
  const when = formatNoteWhen(note.date);
  const meta = [note.author, note.department || encounter?.department, when].filter(Boolean).join(" · ");
  const visitText = encounter ? [recordTypeLabel(encounter), formatDateOnly(encounter.start)].filter(Boolean).join(" · ") : "";
  const back = backLabel ?? (visitText || "Care & Treatment");

  const viewOriginal = (source: Source) => {
    const index = note.sources.indexOf(source);
    setSourceIndex(index < 0 ? 0 : index);
    setMode((m) => (m === "note" ? "original" : m));
  };

  // The marks are rendered with the text; once they're on screen, bring the first into view.
  const highlightKey = highlight.join("|");
  useEffect(() => {
    if (!highlightKey || mode === "original") return;
    textRef.current?.querySelector("mark")?.scrollIntoView?.({ behavior: "smooth", block: "center" });
  }, [highlightKey, mode, note.id]);

  const showNote = mode !== "original";
  const showOriginal = mode !== "note";
  const sideBySide = showNote && showOriginal;
  // No sections menu: the note reads top to bottom on its own. "About this note" sits beside
  // it on wide screens; side by side gives the two panes the width and puts it below.
  const gridClass = sideBySide ? "" : "xl:grid-cols-[minmax(0,1fr)_300px]";
  const asideClass = sideBySide ? "grid items-start gap-4 !space-y-0 lg:grid-cols-2" : "xl:sticky xl:top-4 xl:self-start";

  return (
    <div className={pageClass}>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 print:hidden">
        <div className="flex min-w-0 items-center gap-3">
          <PageBackButton onClick={onBack} ariaLabel={`Back to ${back}`} />
          <span className={cn("truncate", subTextClass)}>{back}</span>
        </div>
        {onOpenNote && (previous !== undefined || next !== undefined) && (
          <nav aria-label="Other notes from this visit" className="flex gap-2">
            <Button variant="outline" disabled={!previous} onClick={() => previous && onOpenNote(previous)} className={outlineButtonClass}>
              <Icon icon="lucide:arrow-left" className="h-4 w-4" />
              Previous note
            </Button>
            <Button variant="outline" disabled={!next} onClick={() => next && onOpenNote(next)} className={outlineButtonClass}>
              Next note
              <Icon icon="lucide:arrow-right" className="h-4 w-4" />
            </Button>
          </nav>
        )}
      </div>

      {/* Header */}
      <header className={cn(cardClass, "mb-4 print:hidden")}>
        {/* Top-aligned: opening "Where this came from" grows the text below, never moves the icon. */}
        <div className="flex flex-wrap items-start gap-4">
          <div className={cn(redIconBoxClass, "h-14 w-14")}>
            <Icon icon="lucide:file-text" className="h-7 w-7 !text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="!mb-0 text-xl font-extrabold text-[#0A2947]">{type}</h1>
              {note.category !== "note" && (
                <span className={concernLabelClass}>{note.category === "report" ? "Report" : "Summary"}</span>
              )}
            </div>
            {meta && <p className={cn("!mb-0 mt-1", subTextClass)}>{meta}</p>}
            <WhereFrom sources={note.sources} onViewOriginal={viewOriginal} className="mt-1" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <PillTabs tabs={VIEW_MODES} active={mode} onChange={setMode} label="Note view" />
          </div>
        </div>
      </header>

      {/* Above the grid, not a cell of it: a cell would push the note into the side column. */}
      {unsure && (
        <div role="status" className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[#E5E7EB] bg-[#FEF6DC] px-4 py-3 text-sm text-[#946200] print:hidden">
          <span>Some text in this note may not have been read correctly. Compare with the original.</span>
          {!sideBySide && (
            <button type="button" onClick={() => setMode("both")} className="font-semibold underline">
              Compare side by side
            </button>
          )}
        </div>
      )}

      {/* Side by side needs the width, so "About this note" moves below the two panes then. */}
      <div className={cn("grid gap-4", gridClass)}>
        <div className={cn("min-w-0 grid content-start gap-4", sideBySide && "lg:grid-cols-2 lg:items-start")}>
          {showNote && (
            <NoteBody
              textRef={textRef}
              sections={note.sections}
              format={note.bodyFormat}
              pending={note.bodyPending}
              scroll={sideBySide}
              signature={note.signatureLine}
              heading={[type, meta].filter(Boolean).join(" · ")}
              terms={highlight}
            />
          )}
          {showOriginal && (
            note.sources[sourceIndex] ? (
              <OriginalViewer
                source={note.sources[sourceIndex]}
                sources={note.sources}
                index={sourceIndex}
                onPick={setSourceIndex}
                actingAsProfileId={actingAsProfileId}
              />
            ) : (
              <EmptyState
                icon={<Icon icon="lucide:file-x" className="h-12 w-12 text-[#9CA3AF]" />}
                title="Original file not available"
                description="We don't have a source file for this note."
              />
            )
          )}
        </div>

        <aside className={cn("min-w-0 space-y-4 print:hidden", asideClass)}>
          {(note.author || visitText || note.sources[0]) && (
            <section aria-labelledby="note-about" className={cardClass}>
              <h2 id="note-about" className={cn(sectionHeadingClass, "!mb-2 !text-base")}>About this note</h2>
              <dl className="!mb-0">
                {note.author && <AboutRow label="Written by" value={note.author} />}
                {visitText ? (
                  <AboutRow
                    label="Visit"
                    value={onOpenVisit
                      ? <button type="button" onClick={onOpenVisit} className={cn(textLinkClass, "!text-sm")}>{visitText}</button>
                      : visitText}
                  />
                ) : note.suggestedEncounterId ? (
                  <AboutRow label="Visit" value="Waiting for you to confirm" />
                ) : null}
                {note.sources[0]?.label && <AboutRow label="From" value={note.sources[0].label} />}
              </dl>
              {encounter && note.linkConfidence !== "high" && (
                <p className={cn("!mb-0 mt-2", inferredNoticeClass)}>Linked by date and clinician</p>
              )}
            </section>
          )}

        </aside>
      </div>
    </div>
  );
}

// function(NoteBody)
/**
 * The note's sections as written, the signature line, and "Shown exactly as
 * written in your record." Paragraphs keep their printed line breaks.
 *
 * @param sections - The note's sections.
 * @param format - How the bodies are written.
 * @param pending - The text is still being read from the original (a scan).
 * @param signature - The note's own signature line(s).
 * @param heading - Printed above the text when the reading view is printed.
 */
function NoteBody({ textRef, sections, format, pending = false, scroll = false, signature, heading, terms = [] }: {
  textRef: RefObject<HTMLElement | null>;
  sections: NoteSection[];
  format: NoteBodyFormat;
  pending?: boolean;
  /** Side by side: the same height as the file viewer beside it, scrolling inside. */
  scroll?: boolean;
  signature?: string;
  heading: string;
  /** Search words to mark. */
  terms?: string[];
}) {
  if (sections.length === 0 && pending) {
    return (
      <EmptyState
        icon={<Icon icon="lucide:hourglass" className="h-12 w-12 text-[#9CA3AF]" />}
        title="Original available, text coming"
        description="We're still reading the text of this document. You can open the original now."
      />
    );
  }
  if (sections.length === 0) {
    return (
      <EmptyState
        icon={<Icon icon="lucide:file-text" className="h-12 w-12 text-[#9CA3AF]" />}
        title="No text to show"
        description="We couldn't read text from this note. Open the original to see it."
      />
    );
  }

  // One document card, sections split by thin lines: reads like a note, not a stack of boxes.
  return (
    <article
      ref={textRef}
      aria-label="Note text"
      className={cn(cardClass, "min-w-0 p-6 sm:p-8", scroll && "lg:max-h-[calc(75vh+5.5rem)] lg:overflow-y-auto")}
    >
      <p className={cn("hidden !mb-4 print:block", titleClass)}>{heading}</p>
      {format === "ocr" && (
        <p className={cn("!mb-5 inline-flex items-center gap-1.5", navyChipClass)}>
          <Icon icon="lucide:scan-text" className="h-3.5 w-3.5" />
          Text read automatically from a scan
        </p>
      )}
      {sections.map((section, i) => (
        <section
          key={section.key}
          id={sectionId(section.key)}
          className={cn("scroll-mt-4", i > 0 && cn("mt-6 pt-6", dividerClass))}
        >
          <h2 className={cn("!mb-0 text-base", titleClass)}>{section.title}</h2>
          <SectionContent section={section} format={format} terms={terms} />
        </section>
      ))}
      {signature && (
        <footer className={cn("mt-8 whitespace-pre-wrap pt-4 text-sm font-semibold text-[#0A2947]", dividerClass)}><Highlighted text={signature} terms={terms} /></footer>
      )}
      <p className={cn("!mb-0 mt-6 flex items-center gap-1.5 pt-4", dividerClass, faintTextClass)}>
        <Icon icon="lucide:info" className="h-3.5 w-3.5 shrink-0" />
        Shown exactly as written in your record.
      </p>
    </article>
  );
}

// function(SectionContent)
/** A section as printed: its blocks (paragraphs, tables) when the server sent them, else its body; search words marked. */
function SectionContent({ section, format, terms }: { section: NoteSection; format: NoteBodyFormat; terms: string[] }) {
  if (section.blocks && section.blocks.length > 0) {
    return (
      <div className="mt-2 space-y-3">
        {section.blocks.map((block, i) =>
          block.type === "text" ? (
            <p key={i} className={cn("!mb-0 whitespace-pre-wrap break-words leading-relaxed", readableClass, bodyTextClass)}>
              <Highlighted text={reflowText(block.text)} terms={terms} />
            </p>
          ) : (
            <div key={i} className="max-w-full overflow-x-auto">
              <table className="w-full border-collapse text-[13px]">
                <tbody>
                  {block.rows.map((row, r) => (
                    <tr key={r}>
                      {row.map((cell, c) =>
                        c === 0 ? (
                          <th key={c} scope="row" className="border border-[#E5E7EB] bg-[#F3F4F6] px-3 py-2 text-left align-top font-semibold text-[#6B7280]">
                            <Highlighted text={cell} terms={terms} />
                          </th>
                        ) : (
                          <td key={c} className="border border-[#E5E7EB] px-3 py-2 align-top text-[#111827]">
                            <Highlighted text={cell} terms={terms} />
                          </td>
                        ),
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ),
        )}
      </div>
    );
  }
  if (format === "html") {
    const html = highlightHtml(sanitizeNoteHtml(section.body), terms, markClass);
    return <div className={cn("mt-2", readableClass, htmlBodyClass)} dangerouslySetInnerHTML={{ __html: html }} />;
  }
  return (
    <p className={cn("!mb-0 mt-2 whitespace-pre-wrap leading-relaxed", readableClass, bodyTextClass)}>
      <Highlighted text={reflowText(section.body)} terms={terms} />
    </p>
  );
}

// function(Highlighted)
/** Text with the search words in <mark>, rendered by React (never patched in the page afterwards). */
function Highlighted({ text, terms }: { text: string; terms: string[] }) {
  if (terms.length === 0) return <>{text}</>;
  return (
    <>
      {splitMatches(text, terms).map((part, i) =>
        part.match ? <mark key={i} className={markClass}>{part.text}</mark> : <Fragment key={i}>{part.text}</Fragment>,
      )}
    </>
  );
}

// function(AboutRow)
function AboutRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className={cn("grid grid-cols-[auto_minmax(0,1fr)] items-baseline gap-4 py-2.5 text-sm first:border-t-0", dividerClass)}>
      <dt className="whitespace-nowrap text-[#6B7280]">{label}</dt>
      <dd className="!mb-0 break-words text-right font-semibold text-[#0A2947] first-letter:uppercase">{value}</dd>
    </div>
  );
}
